import { Injectable, OnDestroy } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { firstValueFrom, of, Subscription } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { UserPermissionsService } from '../services/user-permissions.service';
import { LoggerService } from '../../logging/logger.service';

/**
 * AuthGuard: protects routes by ensuring a valid session exists.
 * If no session is found, initiates SSO login (AzureAD) and blocks activation.
 * When a valid session exists, syncs user permissions.
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, OnDestroy {
  private inProgress = false;
  private permissionsSynced = false;
  private syncInProgress = false;
  private syncSubscription?: Subscription;

  constructor(
    private auth: AuthService,
    private userPermissionsService: UserPermissionsService,
    private router: Router,
    private logger: LoggerService,
  ) {}

  ngOnDestroy(): void {
    this.syncSubscription?.unsubscribe();
  }

  async canActivate(): Promise<boolean | UrlTree> {
    try {
      const session = await firstValueFrom(
        this.auth.amplifyGetUserSession().pipe(
          catchError((err) => {
            this.logger.error('AuthGuard session error', err);
            return of(undefined);
          }),
        ),
      );
      // Narrow expected shape (partial typing to avoid explicit any)
      type PartialSession = { tokens?: { accessToken?: unknown } } | undefined;
      const s: PartialSession = session as PartialSession;
      const hasToken = !!s?.tokens?.accessToken;
      if (hasToken) {
        // Sync user permissions on first successful authentication
        if (!this.permissionsSynced && !this.syncInProgress) {
          this.syncPermissions();
        }
        return true;
      }
    } catch (err) {
      this.logger.error('AuthGuard: exception while obtaining session', err);
    }
    if (!this.inProgress) {
      this.inProgress = true;
      this.logger.info('AuthGuard: no valid session; initiating SSO login');
      this.auth.loginSSO('AzureAD');
    }
    return false;
  }
  private syncPermissions(): void {
    this.syncInProgress = true;
    this.syncSubscription = this.userPermissionsService
      .syncUserPermissions()
      .pipe(
        take(1),
        catchError((err) => {
          this.logger.error('AuthGuard: Failed to sync user permissions', err);
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.permissionsSynced = true;
        this.syncInProgress = false;
        if (response) {
          this.logger.info('AuthGuard: User permissions synced successfully', response);
        }
        // Check global access first, then feature-specific permissions
        this.checkGlobalAccess();
      });
  }

  /**
   * Checks if user has ANY BCR role for global access control.
   * If user has no BCR roles, redirects to access denied page.
   * If user has at least one BCR role, proceeds to check feature permissions.
   */
  private checkGlobalAccess(): void {
    this.auth
      .getUserPrid()
      .pipe(
        switchMap((prid) => this.userPermissionsService.checkGlobalAccess(prid)),
        catchError(() => {
          this.userPermissionsService.hasAnyBcrRole.set(false);
          this.router.navigate(['/access-denied']);
          return of({ has_permission: false });
        }),
      )
      .subscribe((response) => {
        const hasAccess = !!response?.has_permission;
        this.userPermissionsService.hasAnyBcrRole.set(hasAccess);

        if (!hasAccess) {
          this.logger.warn('AuthGuard: User has no BCR roles, redirecting to access denied');
          this.router.navigate(['/access-denied']);
        } else {
          this.logger.info('AuthGuard: User has BCR role access, checking feature permissions');
          // Get prid again for checkUserPermissions
          this.auth.getUserPrid().subscribe((prid) => this.checkUserPermissions(prid));
        }
      });
  }

  /**
   * Checks user permissions for various features.
   * Called after global access is validated.
   *
   * @param prid - User PRID (username before @)
   */
  private checkUserPermissions(prid: string): void {
    // Check Legal Entity List status dropdown permission
    this.userPermissionsService.checkPermission(
      prid,
      ['BCR - PST member'],
      ['Legal Entity List'],
      this.userPermissionsService.hasStatusDropdownPermission,
    );

    // Check Legal Entity Documents upload permission (Replace File button)
    this.userPermissionsService.checkPermission(
      prid,
      ['BCR - PST member'],
      ['Legal Entity List'],
      this.userPermissionsService.hasUploadDocumentPermission,
    );
  }
}
